// src/ctxReflectorV2/components/Reporting/ReportingPage.tsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import { useLocation } from "react-router-dom";

// UI components
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, {
  ParsedFilter,
} from "@ui-components/Searchbar/GenericSearchbar";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

// Icons
import { ReactComponent as IconEdit } from "@assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconPlus } from "@shared/assets/Icons/IconPlusV3.svg";

// Data and types
import {
  ReportingServer,
  columns as baseColumns,
} from "./ReportingData";

// API functions - V2
import {
  getSyslogServers,
  createSyslogServer,
  updateSyslogServer,
  deleteSyslogServer,
  SyslogServerRequest,
} from "./request/request";

// API functions - V1 (dynamically imported when needed)
import {
  getSyslogServersV1,
  getSyslogServerV1,
  createSyslogServerV1,
  updateSyslogServerV1,
  deleteSyslogServerV1,
  SyslogServerRequestV1,
} from "../../../ctxReflector/components/Instances/views/Syslog/request/request";

// V1 subscription fetch
import { fetchSubscriptions } from "../../../ctxReflector/components/Instances/views/HighAvailability/request/request";

// Filter helpers
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from "../../helpers/tableViewport";

/** SideNav + title + Add button + searchbar + card padding (CTX-331). */
const REPORTING_CHROME_PX = 300;

// Field specifications for filtering
const reportingFields: FilterField<ReportingServer>[] = [
  {
    label: "Server IP",
    key: "serverIp",
    type: "string",
    accessor: (row) => row.serverIp,
  },
  {
    label: "Port",
    key: "port",
    type: "number",
    accessor: (row) => row.port,
  },
  {
    label: "Description",
    key: "description",
    type: "string",
    accessor: (row) => row.description,
  },
  {
    label: "Associated CTX-R's",
    key: "associatedCtxrs",
    type: "number",
    accessor: (row) => row.associatedCtxrs,
  },
];

function ReportingPage() {
  const { isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const location = useLocation();
  // Initialize as true to show skeleton immediately on first load
  const [isFetching, setIsFetching] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);
  
  // Detect if we're on v1 route (/reflector/reporting) or v2 route (/reflector-v2/reporting)
  const isV1 = location.pathname.includes("/reflector/reporting") && !location.pathname.includes("/reflector-v2/reporting");

  // Data state
  const [reportingServers, setReportingServers] = useState<ReportingServer[]>(
    []
  );

  // Filter state
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);

  // Form states
  const [editingServer, setEditingServer] = useState<ReportingServer | null>(
    null
  );
  const [formData, setFormData] = useState({
    serverIp: "",
    port: "",
    protocol: "udp" as "udp" | "tcp",
    description: "",
  });

  // Alert states
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return reportingFields.map((f) => f.label);
  }, []);

  // Build maps for filter normalization
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<ReportingServer>>();
    for (const f of reportingFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, []);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of reportingFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, []);

  // Normalize filters
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<ReportingServer>(normalizedFilters, reportingFields),
    [normalizedFilters]
  );

  // Apply predicate to filter data
  const filteredServers = useMemo(
    () => reportingServers.filter(predicate),
    [reportingServers, predicate]
  );

  // Fetch subscription for V1 on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!isV1 || !tenantId) return;
      try {
        const token = await getAccessTokenSilently();
        const resp = await fetchSubscriptions(token, tenantId);
        const subscriptions = resp.data || [];
        // Use the first subscription's ID, or default to 1 if none available
        if (subscriptions.length > 0 && subscriptions[0].id) {
          setSubscriptionId(subscriptions[0].id);
        } else {
          setSubscriptionId(1); // Fallback to default
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        // Fallback to default subscriptionId
        setSubscriptionId(1);
      }
    };

    fetchSubscription();
  }, [isV1, tenantId, getAccessTokenSilently]);

  const loadReportingServers = useCallback(async () => {
    if (!tenantId) {
      setIsFetching(false);
      setFetchError(null);
      return;
    }
    // For V1, wait for subscriptionId to be loaded
    if (isV1 && !subscriptionId) return;

    try {
      setIsFetching(true);
      setFetchError(null);
      const token = await getAccessTokenSilently();

      let servers: ReportingServer[] = [];

      if (isV1 && subscriptionId) {
        const response = await getSyslogServersV1(subscriptionId, tenantId, token);
        const data = response.data?.data || [];
        servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.ctxr_amount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
      } else {
        const response = await getSyslogServers(tenantId, token);
        const data = response.data?.data || [];
        servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.reflectorInstanceCount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
      }

      setReportingServers(servers);
    } catch (error) {
      console.error("Error fetching syslog servers:", error);
      setFetchError("Failed to load syslog servers. Please try again.");
    } finally {
      setIsFetching(false);
    }
  }, [tenantId, getAccessTokenSilently, isV1, subscriptionId]);

  useEffect(() => {
    void loadReportingServers();
  }, [loadReportingServers]);

  // Handle Add button click
  const handleAddClick = () => {
    setFormData({
      serverIp: "",
      port: "",
      protocol: "udp",
      description: "",
    });
    setEditingServer(null);
    setIsAddModalOpen(true);
  };

  // Handle Edit button click
  const handleEditClick = async (server: ReportingServer) => {
    if (isV1 && tenantId && subscriptionId) {
      // For V1, fetch fresh data to prefill modal
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getSyslogServerV1(subscriptionId, server.id, tenantId, token);
        const serverData = response.data;
        
        setEditingServer(server);
        setFormData({
          serverIp: serverData.ip || server.serverIp,
          port: (serverData.port || server.port).toString(),
          protocol: serverData.protocol || "udp",
          description: serverData.description || server.description,
        });
        setIsEditModalOpen(true);
      } catch (error) {
        console.error("Error fetching syslog server details:", error);
        setAlertVariant("error");
        setAlertTitle("Error");
        setAlertDescription("Failed to load syslog server details. Please try again.");
        setIsResultAlertOpen(true);
      } finally {
        setIsFetching(false);
      }
    } else {
      // For V2, use the data we already have from the table row
      setEditingServer(server);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- row may include protocol from API
      const serverWithProtocol = server as any;
      setFormData({
        serverIp: server.serverIp,
        port: server.port.toString(),
        protocol: serverWithProtocol.protocol || "udp",
        description: server.description,
      });
      setIsEditModalOpen(true);
    }
  };

  // Handle Delete button click
  const handleDeleteClick = (server: ReportingServer) => {
    setEditingServer(server);
    setIsDeleteConfirmOpen(true);
  };

  // Handle Save (Add or Edit)
  const handleSave = async () => {
    if (!tenantId) return;
    
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      
      if (isV1) {
        // V1 API
        if (!subscriptionId) {
          console.error("Subscription ID not available");
          setAlertVariant("error");
          setAlertTitle("Error");
          setAlertDescription("Subscription ID is required. Please try again.");
          setTimeout(() => {
            setIsResultAlertOpen(true);
          }, 100);
          return;
        }
        
        const payload: SyslogServerRequestV1 = {
          ip: formData.serverIp,
          port: parseInt(formData.port) || 514,
          protocol: formData.protocol,
          description: formData.description,
        };

        if (editingServer) {
          // Edit mode
          await updateSyslogServerV1(subscriptionId, editingServer.id, tenantId, token, payload);
          setAlertVariant("success");
          setAlertTitle("Updated!");
          setAlertDescription("Syslog server has been updated successfully.");
        } else {
          // Add mode
          await createSyslogServerV1(subscriptionId, tenantId, token, payload);
          setAlertVariant("success");
          setAlertTitle("Added!");
          setAlertDescription("Syslog server has been added successfully.");
        }

        // Refresh the list for V1
        const response = await getSyslogServersV1(subscriptionId, tenantId, token);
        const data = response.data?.data || [];
        const servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.ctxr_amount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
        setReportingServers(servers);
      } else {
        // V2 API
        const payload: SyslogServerRequest = {
          ip: formData.serverIp,
          port: parseInt(formData.port) || 514,
          protocol: formData.protocol,
          description: formData.description,
        };

        if (editingServer) {
          // Edit mode
          await updateSyslogServer(token, editingServer.id, tenantId, payload);
          setAlertVariant("success");
          setAlertTitle("Updated!");
          setAlertDescription("Syslog server has been updated successfully.");
        } else {
          // Add mode
          await createSyslogServer(tenantId, token, payload);
          setAlertVariant("success");
          setAlertTitle("Added!");
          setAlertDescription("Syslog server has been added successfully.");
        }

        // Refresh the list for V2
        const response = await getSyslogServers(tenantId, token);
        const data = response.data?.data || [];
        const servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.reflectorInstanceCount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
        setReportingServers(servers);
      }

      // Close modals first, then show alert
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      // Small delay to ensure modal closes before showing alert
      setTimeout(() => {
        setIsResultAlertOpen(true);
      }, 100);
    } catch (error) {
      console.error("Error saving syslog server:", error);
      // Close modals first, then show error alert
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
      setAlertVariant("error");
      setAlertTitle("Error");
      setAlertDescription(
        editingServer
          ? "Failed to update syslog server. Please try again."
          : "Failed to create syslog server. Please try again."
      );
      // Small delay to ensure modal closes before showing alert
      setTimeout(() => {
        setIsResultAlertOpen(true);
      }, 100);
    } finally {
      setIsFetching(false);
    }
  };

  // Handle Delete confirmation
  const handleConfirmDelete = async () => {
    if (!editingServer || !tenantId) return;

    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      
      if (isV1) {
        // V1 API
        if (!subscriptionId) {
          console.error("Subscription ID not available");
          setAlertVariant("error");
          setAlertTitle("Error");
          setAlertDescription("Subscription ID is required. Please try again.");
          setTimeout(() => {
            setIsResultAlertOpen(true);
          }, 100);
          return;
        }
        
        await deleteSyslogServerV1(subscriptionId, editingServer.id, tenantId, token);

        // Refresh the list for V1
        const response = await getSyslogServersV1(subscriptionId, tenantId, token);
        const data = response.data?.data || [];
        const servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.ctxr_amount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
        setReportingServers(servers);
      } else {
        // V2 API
        await deleteSyslogServer(token, editingServer.id, tenantId);

        // Refresh the list for V2
        const response = await getSyslogServers(tenantId, token);
        const data = response.data?.data || [];
        const servers = Array.isArray(data)
          ? data.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- syslog list API row
              (server: any) => ({
                id: server.id?.toString() || Date.now().toString(),
                serverIp: server.ip || "",
                port: server.port || 0,
                description: server.description || "",
                associatedCtxrs: server.reflectorInstanceCount || 0,
                subscription: "",
                protocol: server.protocol || "udp",
              })
            )
          : [];
        setReportingServers(servers);
      }

      // Close delete confirmation modal first, then show alert
      setIsDeleteConfirmOpen(false);
      setAlertVariant("success");
      setAlertTitle("Deleted!");
      setAlertDescription("Syslog server has been deleted successfully.");
      // Small delay to ensure modal closes before showing alert
      setTimeout(() => {
        setIsResultAlertOpen(true);
      }, 100);
    } catch (error) {
      console.error("Error deleting syslog server:", error);
      // Close delete confirmation modal first, then show error alert
      setIsDeleteConfirmOpen(false);
      setAlertVariant("error");
      setAlertTitle("Error");
      setAlertDescription("Failed to delete syslog server. Please try again.");
      // Small delay to ensure modal closes before showing alert
      setTimeout(() => {
        setIsResultAlertOpen(true);
      }, 100);
    } finally {
      setIsFetching(false);
    }
  };

  // Build columns with action handlers
  const columnsWithActions = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.header === "Actions") {
        return {
          ...col,
          renderCell: (row: ReportingServer) => (
            <div className="flex items-center gap-2">
              <GenericButton
                isIconOnly
                icon={<IconEdit className="w-8 h-8" />}
                variant="secondary"
                shape="filled"
                size="sm"
                aria-label="Edit"
                onClick={() => handleEditClick(row)}
              />
              <GenericButton
                isIconOnly
                icon={<IconDelete className="w-6 h-6" />}
                variant="danger"
                shape="filled"
                size="sm"
                aria-label="Delete"
                onClick={() => handleDeleteClick(row)}
              />
            </div>
          ),
        };
      }
      // Ensure Associated CTX-R's column numbers are centered
      if (col.accessor === "associatedCtxrs") {
        return {
          ...col,
          renderCell: (row: ReportingServer) => (
            <div style={{ 
              width: '100%', 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {row.associatedCtxrs.toString()}
            </div>
          ),
        };
      }
      return col;
    });
  }, []);

  const listLoading = isLoading || isFetching || !tenantId;
  const listEmpty =
    Boolean(tenantId) &&
    !listLoading &&
    !fetchError &&
    reportingServers.length === 0;

  return (
    <div className="relative">
      <GenericCard isCollapsible={false} className="flex flex-col">
        {/* Searchbar and Add Syslog Server button */}
        <div className="mb-2 p-4">
          <div className="flex justify-end mb-4">
            <GenericButton
              label="Add Syslog Server"
              variant="brand"
              size="md"
              icon={<IconPlus className="w-5 h-5" />}
              iconPosition="left"
              onClick={handleAddClick}
            />
          </div>
          <GenericSearchbar
            placeholder="Find by attribute or value (case-sensitive)"
            suggestedKeys={suggestedKeys}
            onFiltersChange={setActiveFilters}
            liveFiltering={true}
            debounceMs={200}
          />
        </div>
        <DataStateView
          loading={listLoading}
          error={fetchError}
          isEmpty={listEmpty}
          onRetry={() => void loadReportingServers()}
          emptyTitle="No syslog servers"
          emptyDescription="Add a syslog server to get started."
          loadingContent={
            <TableSkeleton
              columns={baseColumns.length}
              rows={5}
              className="mx-4 mb-4"
            />
          }
        >
          <GenericTable
            columns={columnsWithActions}
            data={filteredServers}
            initialRowsPerPage={getInitialRowsPerPage(filteredServers.length)}
            rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            maxTableBodyHeight={getTableBodyMaxHeight(REPORTING_CHROME_PX)}
          />
        </DataStateView>
      </GenericCard>

      {/* Style to center the Associated CTX-R's column */}
      <style>{`
        /* Center the 4th column header (Associated CTX-R's) */
        table thead tr th:nth-child(4) > div {
          justify-content: center !important;
        }
        table thead tr th:nth-child(4) > div > span {
          width: 100%;
          text-align: center;
        }
        /* Center the 4th column cells */
        table tbody tr td:nth-child(4) {
          text-align: center !important;
        }
      `}</style>

      {/* Add Modal */}
      <GenericModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Syslog Server"
        size="md"
      >
        <div className="space-y-4">
          <GenericInput
            label="Server IP"
            placeholder="Enter server IP"
            value={formData.serverIp}
            onChange={(value) =>
              setFormData({ ...formData, serverIp: value })
            }
          />
          <GenericInput
            label="Port"
            placeholder="Enter port number"
            value={formData.port}
            onChange={(value) => setFormData({ ...formData, port: value })}
            type="number"
          />
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-2">
              Protocol
            </label>
            <GenericDropdown
              items={["udp", "tcp"]}
              value={formData.protocol}
              onSelect={(value) =>
                setFormData({ ...formData, protocol: value as "udp" | "tcp" })
              }
              defaultLabel="Select protocol"
              widthClass="w-full"
            />
          </div>
          <GenericInput
            label="Description"
            placeholder="Enter description"
            value={formData.description}
            onChange={(value) =>
              setFormData({ ...formData, description: value })
            }
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <GenericButton
            label="Cancel"
            variant="secondary"
            size="md"
            onClick={() => setIsAddModalOpen(false)}
          />
          <GenericButton
            label="Add"
            variant="brand"
            size="md"
            onClick={handleSave}
          />
        </div>
      </GenericModal>

      {/* Edit Modal */}
      <GenericModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Syslog Server"
        size="md"
      >
        <div className="space-y-4">
          <GenericInput
            label="Server IP"
            placeholder="Enter server IP"
            value={formData.serverIp}
            onChange={(value) =>
              setFormData({ ...formData, serverIp: value })
            }
          />
          <GenericInput
            label="Port"
            placeholder="Enter port number"
            value={formData.port}
            onChange={(value) => setFormData({ ...formData, port: value })}
            type="number"
          />
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-2">
              Protocol
            </label>
            <GenericDropdown
              items={["udp", "tcp"]}
              value={formData.protocol}
              onSelect={(value) =>
                setFormData({ ...formData, protocol: value as "udp" | "tcp" })
              }
              defaultLabel="Select protocol"
              widthClass="w-full"
            />
          </div>
          <GenericInput
            label="Description"
            placeholder="Enter description"
            value={formData.description}
            onChange={(value) =>
              setFormData({ ...formData, description: value })
            }
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <GenericButton
            label="Cancel"
            variant="secondary"
            size="md"
            onClick={() => setIsEditModalOpen(false)}
          />
          <GenericButton
            label="Save"
            variant="brand"
            size="md"
            onClick={handleSave}
          />
        </div>
      </GenericModal>

      {/* Delete Confirmation Alert */}
      <GenericAlert
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="Once deleted, you will not be able to recover this Syslog Server!"
        cancelLabel="Cancel"
        confirmLabel="Yes, delete it!"
        variant="error"
      />

      {/* Result Alert */}
      <GenericAlert
        isOpen={isResultAlertOpen}
        onClose={() => setIsResultAlertOpen(false)}
        onConfirm={() => setIsResultAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        confirmLabel="OK"
        variant={alertVariant}
        hideCancel={true}
      />
    </div>
  );
}

export default ReportingPage;

